import { useState } from "react";
import { apiAssetUrl } from "@/shared/api";
import { AREA_CLS } from "@/shared/ui/Collapsible";
import type { OrderMarginLine, OrderMarginRow } from "../../economicSummaryApi";
import type { Order } from "../../types";
import { PANEL, TITLE, TITLE_NOTE } from "./stile";

/** Importi it-IT a 2 decimali SENZA valuta: nel ledger la valuta è dichiarata
 *  una volta nell'intestazione. Ripeterla in 8 colonne × N righe allargherebbe
 *  la tabella e romperebbe l'incolonnamento, che è tutto il punto del ledger. */
const NF = new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Decimal-come-stringa del DTO → number, o null se assente/non numerico.
 *  null = «non lo so», MAI 0: uno zero inventato mente sul margine. */
function dec(v: string | null | undefined): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

const CELL = "whitespace-nowrap px-2.5 py-2 text-right";
const TD = `${CELL} border-b border-line`;
// Colonna Prodotto: va a capo e sta a sinistra. Classi SUE, non «CELL +
// whitespace-normal text-left»: in Tailwind fra due classi in conflitto vince
// l'ordine del CSS generato, non quello dell'attributo — e vinceva
// ``nowrap``, che teneva il titolo su una riga e spingeva fuori i numeri.
const PROD = "w-full px-2.5 py-2 text-left";
// Intestazioni in frase normale, non maiuscoletto: «Margine» si riconosce
// dalla sagoma, «MARGINE» va decifrato lettera per lettera.
const TH = `${CELL} border-b border-line-2 bg-paper text-[12px] font-semibold text-ink`;
const NA = `${TD} italic text-ink`;
const TOT = `${CELL} border-t-2 border-line-2 bg-paper-2 font-semibold`;

interface NumCellProps {
  value: number | null;
  /** Classe extra sul valore noto (es. text-red sul margine negativo). */
  className?: string;
  /** Riga piccola sotto il valore: la fonte del costo o il perché del n/d. */
  sub?: string | null;
  title?: string;
  suffix?: string;
}

/** Cella numerica del ledger. Valore assente → «n/d» in corsivo, che si
 *  incolonna sotto la colonna dove le altre righe hanno un numero. */
function NumCell({ value, className = "", sub, title, suffix = "" }: NumCellProps) {
  return (
    <td className={value == null ? NA : `${TD} ${className}`} title={title || undefined}>
      {value == null ? "n/d" : `${NF.format(value)}${suffix}`}
      {sub ? (
        <span className="block text-[11px] font-normal not-italic text-ink">{sub}</span>
      ) : null}
    </td>
  );
}

/** Cella del totale: somma le righe NOTE e dichiara «+ n/d» quando almeno una
 *  riga manca. Un totale parziale reso come numero pieno mentirebbe. */
function TotalCell({ values }: { values: Array<number | null> }) {
  const known = values.filter((v): v is number => v != null);
  if (known.length === 0) return <td className={`${TOT} italic text-ink`}>n/d</td>;
  return (
    <td className={TOT}>
      {NF.format(known.reduce((a, b) => a + b, 0))}
      {known.length < values.length ? <span className="font-normal text-ink"> + n/d</span> : null}
    </td>
  );
}

/** Foto 44px della riga ordine dal proxy backend
 *  ``/api/orders/items/{id}/thumb`` (item 16). Senza id o su 404/errore degrada
 *  al placeholder "IMG" (stesso pattern del Thumbnail card). */
function ItemThumb({ id, alt }: { id?: number | null; alt: string }) {
  const [errored, setErrored] = useState(false);
  const baseCls = "h-11 w-11 shrink-0 overflow-hidden rounded-sm border border-line-2";
  if (id != null && !errored) {
    return (
      <img
        src={apiAssetUrl(`/api/orders/items/${id}/thumb`)}
        alt={alt}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setErrored(true)}
        className={`${baseCls} bg-paper object-cover`}
      />
    );
  }
  return (
    <div
      className={`${baseCls} inline-grid place-items-center font-mono text-[9px] uppercase text-ink-4`}
      style={{
        background:
          "repeating-linear-gradient(135deg, rgb(var(--om-paper-2)), rgb(var(--om-paper-2)) 6px, rgb(var(--om-card)) 6px, rgb(var(--om-card)) 12px)",
      }}
      aria-hidden="true"
    >
      IMG
    </div>
  );
}

/** Ledger delle righe ordine: ogni riga si incolonna sulle stesse colonne, così
 *  il confronto è verticale e il «n/d» cade dove l'altra riga ha un numero.
 *  Tutti i numeri arrivano dai conti per-riga del backend (Piano 5).
 *
 *  Costo per-SKU assente → «n/d» onesto, MAI ripartizione pro-quota (decisione
 *  D8/Q1): spalmare il costo noto sulle righe senza costo inventerebbe un
 *  margine per riga e nasconderebbe il buco. Il margine d'ordine resta in
 *  ContiSection (passa dal supplier_cost reale, non dalla somma di queste). */
export function ProductsSection({ order, row }: { order: Order; row?: OrderMarginRow | null }) {
  // Quattro colonne su undici sono la SCOMPOSIZIONE del netto (IVA, commissione,
  // spedizione, imballo): vere, ma non si guardano a ogni ordine. Tutte insieme
  // a 1366px spingevano Costo e Margine — le due che decidono — fuori dal
  // pannello, dietro uno scroll orizzontale (redesign 2026-10-22).
  const [tutte, setTutte] = useState(false);
  const items = order.items ?? [];
  const lines = row?.lines ?? [];
  const currency = (order.currency || "EUR").toUpperCase();

  const rows = items.map((item, i) => {
    // Il backend emette le lines nell'ordine di order.items: match posizionale
    // con guardia sullo sku (righe duplicate stesso sku ok). Se non combacia la
    // riga resta senza conti: silenzio VOLUTO, non un bug da aggiustare.
    const line: OrderMarginLine | undefined = lines[i]?.sku === item.sku ? lines[i] : undefined;
    // Senza margini caricati il lordo resta noto: è unitPrice × qta, la stessa
    // formula del backend (economics/service.py:167), non un dato in più.
    const gross = line
      ? dec(line.line_gross)
      : item.unitPrice != null
        ? item.unitPrice * item.quantity
        : null;
    const net = line ? dec(line.line_net) : null;
    const cost = line ? dec(line.product_cost) : null;
    const margin = line ? dec(line.margin) : null;
    const reasons = line?.reasons.join(" · ") || null;
    return {
      item,
      gross,
      net,
      // IVA di riga: il backend la calcola come lordo − netto e poi la butta
      // (service.py:274 `_line_vat`, margin_core.py:45) → qui non si inventa.
      vat: gross != null && net != null ? gross - net : null,
      commission: line ? dec(line.commission) : null,
      shipping: line ? dec(line.shipping) : null,
      packaging: line ? dec(line.packaging) : null,
      cost,
      margin,
      marginPercent: line ? dec(line.margin_percent) : null,
      // Costo noto → la fonte ("catalogo"); costo n/d → il perché, che il DTO
      // porta già in reasons[] e che finora l'utente non vedeva mai.
      costSub: line ? (cost != null ? line.cost_source : reasons) : null,
      reasons,
      marginCls:
        margin == null ? "" : margin < 0 ? "font-semibold text-red" : "font-semibold text-green",
    };
  });

  // Somma solo se c'è più di una riga: su mono-riga ripeterebbe la riga sopra.
  const showTotal = rows.length > 1;
  const sumNet = rows.reduce((a, r) => a + (r.net ?? 0), 0);
  const sumMargin = rows.reduce((a, r) => a + (r.margin ?? 0), 0);
  // La % del totale esiste solo se OGNI riga ha un margine: con una riga a n/d
  // il rapporto sarebbe margine-parziale su netto-intero, cioè una % falsa.
  const totalPercent =
    rows.every((r) => r.margin != null) && sumNet > 0 ? (sumMargin / sumNet) * 100 : null;
  // Buco di costo VERO: i conti sono arrivati e a una riga manca il costo. Senza
  // `lines` il costo manca su tutte, ma perché i margini non sono ancora
  // caricati — dirlo lì darebbe la colpa alle righe di un problema di fetch.
  const costGap = lines.length > 0 && rows.some((r) => r.cost == null);

  return (
    <section className={`${PANEL} ${AREA_CLS.merce}`}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className={TITLE}>
          Prodotti e conti per riga{" "}
          <span className={TITLE_NOTE}>
            {items.length} {items.length === 1 ? "riga" : "righe"} · importi in {currency}
          </span>
        </h3>
        {items.length > 0 && (
          <button
            type="button"
            aria-pressed={tutte}
            onClick={() => setTutte((v) => !v)}
            title="Mostra o nascondi IVA, commissione, spedizione e imballo di ogni riga"
            className="rounded-md px-1.5 py-0.5 text-[12.5px] font-semibold text-ink underline decoration-line-2 decoration-2 underline-offset-4 hover:text-ink hover:decoration-ink-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
          >
            {tutte ? "Meno voci" : "Tutte le voci"}
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-[12px] text-ink">Nessuna riga ordine.</div>
      ) : (
        // -mx-4 annulla il padding della sezione: il ledger arriva ai bordi
        // come nel mock. Lo scroll orizzontale sotto i ~700px resta QUI dentro:
        // detail-left ha min-w-0, quindi il body della pagina non scrolla.
        <div className="-mx-4 overflow-x-auto">
          <table
            className={`w-full border-collapse text-[13px] tabular-nums ${tutte ? "min-w-[720px]" : "min-w-[480px]"}`}
          >
            <thead>
              <tr>
                <th
                  className={`${PROD} border-b border-line-2 bg-paper text-[12px] font-semibold text-ink`}
                >
                  Prodotto
                </th>
                <th className={TH}>Qtà</th>
                <th className={TH}>Lordo</th>
                {tutte && <th className={TH}>IVA</th>}
                <th className={TH}>Netto</th>
                {tutte && (
                  <>
                    <th className={TH}>Commis.</th>
                    <th className={TH}>Sped.</th>
                    <th className={TH}>Imballo</th>
                  </>
                )}
                <th className={TH}>Costo</th>
                <th className={TH}>Margine</th>
                <th className={TH}>%</th>
              </tr>
            </thead>
            <tbody className="[&>tr:last-child>td]:border-b-0">
              {rows.map((r, i) => (
                <tr key={`${r.item.sku}-${i}`} className="even:bg-paper/60">
                  <td className={`${PROD} border-b border-line`}>
                    <div className="flex items-start gap-2.5">
                      <ItemThumb id={r.item.id} alt={r.item.title} />
                      <div className="min-w-0">
                        <div className="font-semibold leading-[1.35] text-ink">{r.item.title}</div>
                        <div className="mt-1 break-all font-mono text-[11px] text-ink">
                          {r.item.sku}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={TD}>{r.item.quantity}</td>
                  <NumCell value={r.gross} />
                  {tutte && <NumCell value={r.vat} />}
                  <NumCell value={r.net} />
                  {tutte && (
                    <>
                      <NumCell value={r.commission} />
                      <NumCell value={r.shipping} />
                      <NumCell value={r.packaging} />
                    </>
                  )}
                  <NumCell value={r.cost} sub={r.costSub} title={r.reasons ?? undefined} />
                  <NumCell value={r.margin} className={r.marginCls} />
                  <NumCell value={r.marginPercent} className={r.marginCls} suffix="%" />
                </tr>
              ))}
              {showTotal && (
                <tr>
                  <td className={`${PROD} border-t-2 border-line-2 bg-paper-2 font-semibold`}>
                    Somma onesta delle righe
                    <span className="block text-[11px] font-normal text-ink">
                      {costGap
                        ? "le righe senza costo non entrano: nessuna stima al loro posto"
                        : "somma delle righe qui sopra, non il margine d'ordine"}
                    </span>
                  </td>
                  <td className={TOT}>{rows.reduce((a, r) => a + r.item.quantity, 0)}</td>
                  <TotalCell values={rows.map((r) => r.gross)} />
                  {tutte && <TotalCell values={rows.map((r) => r.vat)} />}
                  <TotalCell values={rows.map((r) => r.net)} />
                  {tutte && (
                    <>
                      <TotalCell values={rows.map((r) => r.commission)} />
                      <TotalCell values={rows.map((r) => r.shipping)} />
                      <TotalCell values={rows.map((r) => r.packaging)} />
                    </>
                  )}
                  <TotalCell values={rows.map((r) => r.cost)} />
                  <TotalCell values={rows.map((r) => r.margin)} />
                  {totalPercent == null ? (
                    <td className={`${TOT} italic text-ink`}>—</td>
                  ) : (
                    <td className={TOT}>{`${NF.format(totalPercent)}%`}</td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {costGap && (
        <div className="mt-3 rounded-lg border border-amber/30 bg-amber-soft px-3 py-2 text-[12.5px] text-ink">
          Righe senza costo: il margine d'ordine non è calcolabile. Il costo non viene spalmato
          pro-quota.
        </div>
      )}
    </section>
  );
}
